import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  SyncOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd";
import {
  Button,
  Card,
  Modal,
  Popconfirm,
  Result,
  Space,
  Spin,
  Table,
  TableColumnType,
  Tag,
  Typography,
  Upload,
} from "antd";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ModalQCEvaluation from "../../components/modal/QualityControl/ModalQCEvaluation";
import { PriorityMark, QCStatus } from "../../enum/common";
import {
  useAcceptQCRequest,
  useBulkEvaluateQCRequest,
  useGetQCRequestDetail,
} from "../../query/QcQuery";
import { RootState } from "../../store";
import { generateQCEvaluationTemplate } from "../../utils/qcExcelHelper";
import { renderQCStatusTag } from "../../utils/qcHelper";
import { notify } from "../../utils/toast";

const { Text, Title } = Typography;
interface ISelectedPartN {
  id: number;
  requestId: number;
  qcStatus: QCStatus;
  partNumber: string;
  projectCode: string;
  project: string;
  description: string;
  manufacturer: string;
  actualQuantity: number;
  unit: string;
  location: {
    id: number;
    code: string;
    warehouse: {
      warehouseCode: string;
    };
  };
  supplierType: string;
  purchaser: string;
}
const QualityControlRequestDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const idNumber = Number(id);
  const { data, isLoading } = useGetQCRequestDetail(idNumber);
  const acceptQCRequest = useAcceptQCRequest();
  const department = useSelector(
    (state: RootState) => state.auth.user?.user?.department.departmentCode,
  );

  const role = useSelector((state: RootState) => state.auth.user?.user?.role);
  const [openQCEvaluation, setOpenQCEvaluation] = useState<boolean>(false);
  const [selectedPartRecord, setSelectedPartRecord] =
    useState<ISelectedPartN>();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const bulkEvaluate = useBulkEvaluateQCRequest();

  const handleAcceptRequest = async () => {
    try {
      await acceptQCRequest.mutateAsync(idNumber);
    } catch (error: any) {
      notify.error(
        error?.response?.data?.message || "Không thể chấp nhận yêu cầu",
      );
    }
  };

  const handleUploadFile = async (file: File) => {
    try {
      await bulkEvaluate.mutateAsync({
        requestId: idNumber,
        file: file,
      });

      setFileList([]);
    } catch (error: any) {
      notify.error(
        error?.response?.data?.message || "Không thể upload file đánh giá",
      );
    }
  };

  const uploadProps: UploadProps = {
    accept: ".xlsx,.xls",
    maxCount: 1,
    fileList: fileList,
    beforeUpload: (file) => {
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";

      if (!isExcel) {
        notify.error("Chỉ chấp nhận file Excel (.xlsx, .xls)");
        return Upload.LIST_IGNORE;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        notify.error("File không được vượt quá 5MB");
        return Upload.LIST_IGNORE;
      }

      setFileList([file as UploadFile]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  const handleUploadSubmit = () => {
    if (fileList.length === 0) {
      notify.warning("Vui lòng chọn file Excel");
      return;
    }

    const file = fileList[0] as any;
    handleUploadFile(file.originFileObj || file);
  };

  const getPriorityLabel = (priority: PriorityMark) => {
    switch (priority) {
      case PriorityMark.HIGH:
        return "Cao";
      case PriorityMark.MEDIUM:
        return "Trung bình";
      case PriorityMark.LOW:
        return "Thấp";
      default:
        return priority;
    }
  };

  const getPriorityColor = (priority: PriorityMark) => {
    switch (priority) {
      case PriorityMark.HIGH:
        return "red";
      case PriorityMark.MEDIUM:
        return "orange";
      case PriorityMark.LOW:
        return "green";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PENDING":
        return "Chờ xử lý";
      case "PROCESSING":
        return "Đang xử lý";
      case "DONE":
        return "Hoàn thành";
      default:
        return status;
    }
  };

  const columns: TableColumnType<any>[] = [
    {
      title: "STT",
      key: "index",
      align: "center",
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Trạng thái QC",
      dataIndex: ["qcStatus"],
      key: "qcStatus",
      width: 120,
      render: (qcStatus: QCStatus) => renderQCStatusTag(qcStatus),
    },
    {
      title: "Part Number",
      dataIndex: ["partNumber"],
      key: "partNumber",
      width: 150,
      render: (partNumber: string) => (
        <Text strong style={{ color: "#1890ff" }}>
          {partNumber}
        </Text>
      ),
    },

    {
      title: "Mã dự án",
      dataIndex: ["projectCode"],
      key: "projectCode",
      align: "center",
      width: 120,
    },
    {
      title: "Dự án",
      dataIndex: ["project"],
      key: "project",
      width: 150,
    },
    {
      title: "Mô tả",
      dataIndex: ["description"],
      key: "description",
      width: 200,
    },
    {
      title: "Nhà sản xuất",
      dataIndex: ["manufacturer"],
      key: "manufacturer",
      align: "center",
      width: 150,
    },
    {
      title: "Loại nhà cung cấp",
      dataIndex: ["supplierType"],
      key: "supplierType",
      align: "center",
      width: 150,
    },
    {
      title: "Purchaser",
      dataIndex: ["purchaser"],
      key: "purchaser",
      align: "center",
      width: 150,
    },
    {
      title: "Số lượng hàng về",
      dataIndex: ["actualQuantity"],
      key: "actualQuantity",
      align: "center",
      width: 100,
      render: (quantity: number) => (
        <Text strong style={{ color: "#fa8c16" }}>
          {quantity}
        </Text>
      ),
    },

    {
      title: "Đơn vị",
      dataIndex: ["unit"],
      key: "unit",
      align: "center",
      width: 80,
    },
    {
      title: "Vị trí",
      dataIndex: ["location", "code"],
      key: "location",
      align: "center",
      width: 120,
      render: (code: string, record: any) => (
        <div>
          <div>
            <Text strong>{code}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.location?.warehouse?.warehouseCode}
          </Text>
        </div>
      ),
    },
    {
      title: "Hành động",
      key: "action",
      align: "center",
      width: 150,
      fixed: "right",
      render: (_: any, record: any) => {
        const isDisabled =
          record.qcStatus === "PENDING" ||
          record.qcStatus === "PASSED" ||
          record.qcStatus === "REJECT" ||
          record.qcStatus === "PARTIAL_REJECT";

        const hasPermission =
          department === "QC" || role === "ADMIN" || role === "SUPER_ADMIN";

        if (!hasPermission) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Không có quyền
            </Text>
          );
        }

        if (isDisabled) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Không thể thao tác
            </Text>
          );
        }

        return (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              setSelectedPartRecord({ ...record, requestId: idNumber });
              setOpenQCEvaluation(true);
            }}
          >
            Đánh giá QC
          </Button>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="p-4 flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <Result
        status="404"
        title="404"
        subTitle="Không tìm thấy yêu cầu kiểm tra chất lượng"
        extra={
          <Button type="primary" onClick={() => navigate(-1)}>
            Quay lại
          </Button>
        }
      />
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          Quay lại
        </Button>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <Title
              level={3}
              style={{
                margin: 0,
                fontFamily: "Chakra Petch",
                textTransform: "uppercase",
              }}
              className="dark:text-white"
            >
              {data.name}
            </Title>
            <Space wrap className="mt-2">
              <Tag icon={<UserOutlined />} color="cyan">
                Người tạo: {data.createdByUser.fullName}
              </Tag>
              <Tag color="blue">AWB: {data.awb}</Tag>
              <Tag color={getPriorityColor(data.priority)}>
                Mức độ ưu tiên: {getPriorityLabel(data.priority)}
              </Tag>
            </Space>
          </div>
          <div className="text-center">
            <Space direction="vertical" align="start" size="small">
              <Space>
                {data.status === "PROCESSING" &&
                  (department === "QC" ||
                    role === "ADMIN" ||
                    role === "SUPER_ADMIN") && (
                    <>
                      <Button
                        type="default"
                        icon={<DownloadOutlined />}
                        onClick={() =>
                          generateQCEvaluationTemplate(
                            data.name,
                            data.awb,
                            data.group?.partNumbers || [],
                          )
                        }
                      >
                        Tải template Excel
                      </Button>
                      <Button
                        type="default"
                        icon={<UploadOutlined />}
                        onClick={() => setUploadModalVisible(true)}
                      >
                        Upload file đánh giá
                      </Button>
                    </>
                  )}
                {data.status === "PENDING" &&
                  (department === "QC" ||
                    role === "ADMIN" ||
                    role === "SUPER_ADMIN") && (
                    <Popconfirm
                      title="Xác nhận chấp nhận yêu cầu"
                      description="Bạn có chắc chắn muốn chấp nhận yêu cầu QC này?"
                      onConfirm={handleAcceptRequest}
                      okText="Chấp nhận"
                      cancelText="Hủy"
                    >
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        loading={acceptQCRequest.isPending}
                      >
                        Chấp nhận yêu cầu
                      </Button>
                    </Popconfirm>
                  )}
              </Space>
            </Space>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Card size="small" className="bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 mb-2">
            <UserOutlined className="text-blue-500" />
            <Text strong>Người tạo yêu cầu</Text>
          </div>
          <Text className="text-lg block">{data.createdByUser.fullName}</Text>
        </Card>

        <Card size="small" className="bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleOutlined className="text-green-500" />
            <Text strong>Trạng thái</Text>
          </div>
          <Text className="text-lg block">{getStatusLabel(data.status)}</Text>
        </Card>

        <Card size="small" className="bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 mb-2">
            <SyncOutlined className="text-orange-500" />
            <Text strong>Mức độ ưu tiên</Text>
          </div>
          <Text className="text-lg block">
            {getPriorityLabel(data.priority)}
          </Text>
        </Card>

        <Card size="small" className="bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleOutlined className="text-purple-500" />
            <Text strong>Số lượng hàng</Text>
          </div>
          <Text className="text-xl block">{data.group.partNumbers.length}</Text>
        </Card>
      </div>

      <Card
        title="Danh sách Part Numbers"
        size="small"
        className="mb-4"
        style={{ borderRadius: 8 }}
      >
        <Table
          dataSource={data.group.partNumbers}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1500 }}
          size="small"
          bordered
        />
      </Card>

      <ModalQCEvaluation
        open={openQCEvaluation}
        record={selectedPartRecord}
        onSuccess={() => {
          setOpenQCEvaluation(false);
          setSelectedPartRecord(undefined);
        }}
        onCancel={() => {
          setOpenQCEvaluation(false);
          setSelectedPartRecord(undefined);
        }}
      />

      <Modal
        title="Upload file đánh giá QC hàng loạt"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setFileList([]);
        }}
        onOk={handleUploadSubmit}
        okText="Upload"
        cancelText="Hủy"
        confirmLoading={bulkEvaluate.isPending}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Chọn file Excel chứa dữ liệu đánh giá QC cho các linh kiện trong yêu
            cầu này.
          </Text>
        </div>

        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: 48, color: "#1890ff" }} />
          </p>
          <p className="ant-upload-text">
            Click hoặc kéo file vào đây để upload
          </p>
          <p className="ant-upload-hint">
            Chỉ chấp nhận file Excel (.xlsx, .xls), tối đa 5MB
          </p>
        </Upload.Dragger>

        {fileList.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Text strong>File đã chọn:</Text>
            <div style={{ marginTop: 8 }}>
              <Tag color="blue" icon={<CheckCircleOutlined />}>
                {fileList[0].name}
              </Tag>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default QualityControlRequestDetail;
